// InfoTooltip.tsx
// Small ⓘ icon with hover tooltip showing HTAG metric definitions.
// Uses fixed positioning to escape overflow:hidden containers.

import { useState, useRef, useEffect } from 'react'
import { HTAG_GLOSSARY } from '../../lib/htag-glossary'

interface InfoTooltipProps {
  term: string
  className?: string
}

export default function InfoTooltip({ term, className }: InfoTooltipProps) {
  const entry = HTAG_GLOSSARY[term]
  const [visible, setVisible] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const iconRef = useRef<HTMLSpanElement>(null)

  if (!entry) return null

  function show() {
    if (!iconRef.current) return
    const rect = iconRef.current.getBoundingClientRect()
    setPos({
      top: rect.top - 8,   // viewport-relative (position: fixed)
      left: rect.left + rect.width / 2,
    })
    setVisible(true)
  }

  function hide() {
    setVisible(false)
  }

  return (
    <>
      <span
        ref={iconRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'default',
          color: '#9CA3AF',
          fontSize: 14,
          lineHeight: 1,
          userSelect: 'none',
        }}
        aria-label={`Definition: ${entry.term}`}
      >
        ⓘ
      </span>

      {visible && (
        <TooltipPortal top={pos.top} left={pos.left} entry={entry} />
      )}
    </>
  )
}

// ─── Portal tooltip rendered at document body level ──────────────────────────

interface TooltipPortalProps {
  top: number
  left: number
  entry: { term: string; definition: string; interpretation?: string }
}

function TooltipPortal({ top, left, entry }: TooltipPortalProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [adjustedLeft, setAdjustedLeft] = useState(left)

  useEffect(() => {
    if (!ref.current) return
    const tooltipWidth = ref.current.offsetWidth
    const viewportWidth = window.innerWidth
    const halfW = tooltipWidth / 2
    let l = left - halfW
    if (l < 8) l = 8
    if (l + tooltipWidth > viewportWidth - 8) l = viewportWidth - tooltipWidth - 8
    setAdjustedLeft(l)
  }, [left])

  const tooltipTop = top - (ref.current?.offsetHeight ?? 0) - 6

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        top: tooltipTop,
        left: adjustedLeft,
        zIndex: 9999,
        maxWidth: 280,
        background: '#1C1A17',
        color: '#F5F2EC',
        fontSize: 12,
        lineHeight: 1.5,
        borderRadius: 6,
        padding: '8px 12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        pointerEvents: 'none',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#F5F2EC' }}>
        {entry.term}
      </div>
      <div>{entry.definition}</div>
      {entry.interpretation && (
        <div style={{ marginTop: 6, color: '#C8A882', fontStyle: 'italic' }}>
          {entry.interpretation}
        </div>
      )}
      {/* Arrow pointing down */}
      <div
        style={{
          position: 'absolute',
          bottom: -6,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '6px solid #1C1A17',
        }}
      />
    </div>
  )
}
